import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2, Package, ShoppingBag, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/data-display/states";
import { Footprints } from "lucide-react";
import { cn, formatKES } from "@/lib/utils";
import { useCart, useCartItems } from "@/lib/cart";
import { orderTotal, reservationTotal, type Order } from "@/types/domain";

const FLAT_FEE_PER_ITEM = 4; // KES flat fee per order line

export function CartSheet({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const items = useCartItems();
  const remove = useCart((s) => s.remove);
  const checkoutAll = useCart((s) => s.checkoutAll);
  const [placing, setPlacing] = useState(false);
  const [confirmedOrders, setConfirmedOrders] = useState<Order[]>([]);

  const subtotal = items.reduce((s, r) => s + reservationTotal(r), 0);
  const fee = items.length * FLAT_FEE_PER_ITEM;
  const earnings = subtotal - fee;

  const placeOrders = () => {
    setPlacing(true);
    void checkoutAll()
      .then((orders) => {
        setPlacing(false);
        if (orders && orders.length > 0) {
          setConfirmedOrders(orders);
        } else {
          requestAnimationFrame(() => onOpenChange(false));
          toast.error("Couldn't place the order", { description: "Please try again." });
        }
      })
      .catch(() => {
        setPlacing(false);
        toast.error("Couldn't place the order", { description: "Please try again." });
      });
  };

  // Show receipt screen after successful checkout
  if (confirmedOrders.length > 0) {
    return (
      <Sheet open={open} onOpenChange={(o) => { if (!o) { setConfirmedOrders([]); onOpenChange(false); } }}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 sm:max-w-md">
          <div className="flex flex-1 flex-col items-center justify-start gap-6 overflow-y-auto px-6 py-8">
            <div className="flex flex-col items-center gap-3 text-center">
              <span className="flex size-16 items-center justify-center rounded-full bg-success/15 text-success">
                <CheckCircle2 className="size-9" />
              </span>
              <div>
                <p className="text-xl font-semibold text-foreground">
                  {confirmedOrders.length} order{confirmedOrders.length > 1 ? "s" : ""} placed!
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Wholesalers have been notified. Stock is confirmed.
                </p>
              </div>
            </div>

            <div className="w-full space-y-3">
              {confirmedOrders.map((o) => {
                const total = orderTotal(o);
                return (
                  <div key={o.id} className="rounded-xl border p-4 space-y-3">
                    <div className="flex items-center gap-3">
                      <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <Package className="size-5" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-foreground">{o.brand} {o.productName}</p>
                        <p className="text-xs text-muted-foreground">UK {o.size} · {o.qty} pair{o.qty > 1 ? "s" : ""} · {o.wholesalerName}</p>
                      </div>
                      <span className="text-xs font-medium text-muted-foreground">{o.reference}</span>
                    </div>
                    <dl className="divide-y rounded-lg border text-sm">
                      <ReceiptRow label="Order total" value={formatKES(total)} />
                      <ReceiptRow label="Platform fee" value={`− ${formatKES(FLAT_FEE_PER_ITEM)}`} muted />
                      <ReceiptRow label="Your earnings" value={formatKES(total - FLAT_FEE_PER_ITEM)} strong />
                    </dl>
                  </div>
                );
              })}
            </div>

            {confirmedOrders.length > 1 && (
              <div className="w-full rounded-xl border bg-muted/40 p-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium text-foreground">Total earnings</span>
                  <span className="font-semibold tabular-nums text-foreground">
                    {formatKES(confirmedOrders.reduce((s, o) => s + orderTotal(o) - FLAT_FEE_PER_ITEM, 0))}
                  </span>
                </div>
              </div>
            )}

            <p className="text-center text-xs text-muted-foreground">
              You'll be notified when each order is confirmed and fulfilled by the wholesaler.
            </p>
          </div>
          <div className="border-t p-4">
            <Button className="w-full" onClick={() => { setConfirmedOrders([]); onOpenChange(false); }}>
              Done
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <ShoppingBag className="size-5" /> Cart
            {items.length > 0 && (
              <span className="rounded-full bg-primary/12 px-2 py-0.5 text-xs font-semibold text-primary tabular-nums">
                {items.length}
              </span>
            )}
          </SheetTitle>
          <SheetDescription>
            Review your selections and check out to place the orders.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-4">
          {items.length === 0 ? (
            <EmptyState
              icon={ShoppingBag}
              title="Your cart is empty"
              description="Add items from the live stock feed to check out."
              action={
                <Button variant="outline" asChild onClick={() => onOpenChange(false)}>
                  <Link to="/reseller">Browse stock feed</Link>
                </Button>
              }
              className="mt-6"
            />
          ) : (
            <ul className="space-y-2.5 py-2">
              {items.map((r) => (
                <li key={r.id} className="flex items-center gap-3 rounded-xl border p-3">
                  <span className="flex size-11 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Footprints className="size-5" />
                  </span>
                  <div className="min-w-0 flex-1 space-y-0.5">
                    <p className="truncate text-sm font-medium">{r.brand} {r.productName}</p>
                    <p className="text-xs text-muted-foreground">
                      UK {r.size} · {r.qty} pair{r.qty > 1 ? "s" : ""} · {r.wholesalerName}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className="text-sm font-semibold tabular-nums">{formatKES(reservationTotal(r))}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-8 text-muted-foreground hover:text-destructive"
                      onClick={() => { void remove(r.id); toast("Removed from cart"); }}
                      aria-label="Remove from cart"
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <SheetFooter className="border-t">
            <dl className="space-y-1.5 pb-2 text-sm">
              <Row label="Subtotal" value={formatKES(subtotal)} />
              <Row label={`Platform fee (${items.length} × KES ${FLAT_FEE_PER_ITEM})`} value={`− ${formatKES(fee)}`} muted />
              <Row label="Your earnings" value={formatKES(earnings)} strong />
            </dl>
            <Button className="w-full" disabled={placing} onClick={placeOrders}>
              {placing && <Loader2 className="size-4 animate-spin" />}
              {placing ? "Placing orders…" : `Check out ${items.length} item${items.length > 1 ? "s" : ""}`}
            </Button>
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Row({ label, value, muted, strong }: { label: string; value: string; muted?: boolean; strong?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={cn("tabular-nums", strong ? "text-base font-semibold text-foreground" : muted ? "text-muted-foreground" : "font-medium")}>
        {value}
      </dd>
    </div>
  );
}

function ReceiptRow({ label, value, muted, strong }: { label: string; value: string; muted?: boolean; strong?: boolean }) {
  return (
    <div className="flex items-center justify-between px-3 py-2">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className={cn("text-sm tabular-nums", strong ? "font-semibold text-foreground" : muted ? "text-muted-foreground" : "font-medium text-foreground")}>
        {value}
      </dd>
    </div>
  );
}
